// components/ApiTrafficConsole.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaSync, FaPlus, FaSearch, FaFilter, FaTimes, FaCopy, FaTrash,
  FaToggleOn, FaToggleOff, FaEdit, FaCheckCircle, FaExclamationTriangle,
  FaClock, FaBolt, FaNetworkWired
} from 'react-icons/fa';
import './ApiTrafficConsole.css';

const defaultEndpointForm = {
  endpoint_key: '',
  nome: '',
  base_url: '',
  enabled: true,
  allowed_methods: ['GET', 'POST', 'PUT', 'DELETE'],
  headers_template: {}
};

const pillClass = (type) => {
  const s = String(type || '').toLowerCase();
  if (['on', 'ativo', 'true'].includes(s)) return 'pill ativo';
  if (['off', 'suspenso', 'false'].includes(s)) return 'pill suspenso';
  if (['2xx', '200', '201', '204', 'ok'].includes(s)) return 'pill recebido';
  if (['4xx', '5xx', 'erro', 'fail'].includes(s)) return 'pill rejeitado';
  return 'pill';
};

function prettyJSON(value) {
  if (value == null) return '';
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

async function copyToClipboard(text, msg = 'Copiado!') {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(msg);
  } catch {
    toast.info('Não consegui copiar automaticamente. Selecione e copie.');
  }
}

export default function ApiTrafficConsole() {
  const [endpoints, setEndpoints] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');

  // filtros
  const [limit, setLimit] = useState(120);
  const [endpointFilter, setEndpointFilter] = useState('');
  const [statusFamily, setStatusFamily] = useState(''); // 2xx/4xx/5xx
  const [methodFilter, setMethodFilter] = useState(''); // GET/POST/PUT/DELETE
  const [search, setSearch] = useState('');

  // endpoint modal
  const [epModalOpen, setEpModalOpen] = useState(false);
  const [editingEp, setEditingEp] = useState(null);
  const [form, setForm] = useState(defaultEndpointForm);
  const [headersText, setHeadersText] = useState('{}'); // editor JSON de headers_template
  const [methodsState, setMethodsState] = useState({
    GET: true, POST: true, PUT: true, DELETE: true
  });

  // drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadAll = async (silent = false) => {
  if (!silent) setLoading(true);
  setErrMsg('');
  try {
    const [e, t] = await Promise.all([
      api.get('/api/api-gateway/endpoints').catch(() => ({ data: [] })),
      api.get(`/api/api-gateway/traffic?limit=${limit}${endpointFilter ? `&endpointId=${endpointFilter}` : ''}`)
         .catch(() => ({ data: [] }))
    ]);

    setEndpoints(Array.isArray(e.data) ? e.data : []);
    setTraffic(Array.isArray(t.data) ? t.data : []);
  } catch (err) {
    setErrMsg('Falha ao carregar console de tráfego.');
  } finally {
    if (!silent) setLoading(false);
  }
};


  useEffect(() => { loadAll(false); }, [limit, endpointFilter]);

  // ---------- Helpers endpoints ----------
  const openNewEndpoint = () => {
    setEditingEp(null);
    setForm(defaultEndpointForm);
    setHeadersText('{}');
    setMethodsState({ GET: true, POST: true, PUT: true, DELETE: true });
    setEpModalOpen(true);
  };

  const openEditEndpoint = (ep) => {
    setEditingEp(ep);
    const allowed = (() => {
      try { return JSON.parse(ep.allowed_methods_json || '["GET","POST","PUT","DELETE"]'); }
      catch { return ['GET','POST','PUT','DELETE']; }
    })();
    const headersObj = (() => {
      try { return JSON.parse(ep.headers_template_json || '{}'); }
      catch { return {}; }
    })();

    setForm({
      endpoint_key: ep.endpoint_key || '',
      nome: ep.nome || '',
      base_url: ep.base_url || '',
      enabled: !!ep.enabled,
      allowed_methods: allowed,
      headers_template: headersObj
    });

    setMethodsState({
      GET: allowed.includes('GET'),
      POST: allowed.includes('POST'),
      PUT: allowed.includes('PUT'),
      DELETE: allowed.includes('DELETE')
    });

    setHeadersText(prettyJSON(headersObj) || '{}');
    setEpModalOpen(true);
  };

  const saveEndpoint = async () => {
    setErrMsg('');
    try {
      if (!form.endpoint_key || !form.nome || !form.base_url) {
        toast.warn('endpoint_key, Nome e Base URL são obrigatórios.');
        return;
      }

      // methods
      const allowed_methods = Object.entries(methodsState)
        .filter(([, v]) => v)
        .map(([k]) => k);

      // headers json
      let headers_template = {};
      try {
        headers_template = headersText?.trim() ? JSON.parse(headersText) : {};
      } catch {
        toast.error('Headers Template precisa ser um JSON válido.');
        return;
      }

      const payload = {
        endpoint_key: form.endpoint_key.trim(),
        nome: form.nome.trim(),
        base_url: form.base_url.trim(),
        enabled: form.enabled ? 1 : 0,
        allowed_methods,
        headers_template
      };

      if (editingEp?.id) {
        await api.put(`/api/api-gateway/endpoints/${editingEp.id}`, payload);
        toast.success('Endpoint atualizado!');
      } else {
        await api.post('/api/api-gateway/endpoints', payload);
        toast.success('Endpoint criado!');
      }

      setEpModalOpen(false);
      await loadAll(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha ao salvar endpoint.';
      setErrMsg(msg);
      toast.error(msg);
    }
  };

  const toggleEnabled = async (ep) => {
    setErrMsg('');
    try {
      await api.put(`/api/api-gateway/endpoints/${ep.id}`, { enabled: ep.enabled ? 0 : 1 });
      toast.success(ep.enabled ? 'Endpoint desabilitado.' : 'Endpoint habilitado.');
      await loadAll(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha ao atualizar endpoint.';
      setErrMsg(msg);
      toast.error(msg);
    }
  };

  const deleteEndpoint = async (ep) => {
    setErrMsg('');
    try {
      const ok = window.confirm(`Tem certeza que deseja excluir o endpoint "${ep.nome}"? Isso apagará também os logs relacionados (ON DELETE CASCADE).`);
      if (!ok) return;

      await api.delete(`/api/api-gateway/endpoints/${ep.id}`);
      toast.success('Endpoint excluído!');
      await loadAll(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha ao excluir endpoint.';
      setErrMsg(msg);
      toast.error(msg);
    }
  };

  // ---------- Tráfego / filtros ----------
  const filteredTraffic = useMemo(() => {
    const q = search.trim().toLowerCase();

    return traffic.filter((l) => {
      if (methodFilter && String(l.method).toUpperCase() !== methodFilter) return false;

      if (statusFamily) {
        const st = Number(l.response_status || 0);
        if (statusFamily === '2xx' && !(st >= 200 && st < 300)) return false;
        if (statusFamily === '4xx' && !(st >= 400 && st < 500)) return false;
        if (statusFamily === '5xx' && !(st >= 500 && st < 600)) return false;
      }

      if (!q) return true;
      const hay = [
        l.id, l.endpoint_nome, l.endpoint_key, l.method, l.path, l.query_string, l.response_status, l.created_at
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [traffic, search, methodFilter, statusFamily]);

  // ---------- Métricas ----------
  const metrics = useMemo(() => {
    const total = traffic.length;
    const errors = traffic.filter(t => Number(t.response_status) >= 400).length;

    const durations = traffic
      .map(t => Number(t.duration_ms || 0))
      .filter(n => Number.isFinite(n) && n >= 0)
      .sort((a, b) => a - b);

    const avg = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
    const p95 = durations.length ? durations[Math.min(durations.length - 1, Math.floor(durations.length * 0.95))] : 0;

    const last = traffic[0]?.created_at || null;

    // top endpoint por volume
    const count = new Map();
    traffic.forEach(t => {
      const key = t.endpoint_nome || '—';
      count.set(key, (count.get(key) || 0) + 1);
    });
    let topName = '—', topVal = 0;
    for (const [k, v] of count.entries()) {
      if (v > topVal) { topVal = v; topName = k; }
    }

    return { total, errors, avg, p95, last, topName, topVal };
  }, [traffic]);

  // ---------- Drawer ----------
  const openLog = async (id) => {
    setErrMsg('');
    try {
      const { data } = await api.get(`/api/api-gateway/traffic/${id}`);
      setSelectedLog(data);
      setDrawerOpen(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha ao buscar detalhe do log.';
      setErrMsg(msg);
      toast.error(msg);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedLog(null), 150);
  };

  // Proxy help
  const proxyExample = useMemo(() => {
    return `/api/api-gateway/proxy/:endpointKey/*splat`;
  }, []);

  return (
    <div className="api2-page">
      {/* Header */}
      <div className="api2-topbar">
        <div>
          <h1>Console de Tráfego & APIs</h1>
          <p className="api2-subtitle">
            Gestão de endpoints (allowlist), proxy controlado e observabilidade (PUT/POST/GET/DELETE).
          </p>
        </div>

        <div className="api2-actions">
          <button className="api2-btn api2-btnGhost" onClick={() => copyToClipboard(proxyExample, 'Rota proxy copiada!')}>
            <FaCopy /> Copiar rota proxy
          </button>

          <button className="api2-btn api2-btnGhost" onClick={openNewEndpoint}>
            <FaPlus /> Novo endpoint
          </button>

          <button className="api2-btn" onClick={() => loadAll(false)}>
            <FaSync /> Atualizar
          </button>
        </div>
      </div>

      {/* Error */}
      {errMsg && (
        <div className="api2-alert">
          <FaExclamationTriangle />
          <span>{errMsg}</span>
          <button className="api2-alertClose" onClick={() => setErrMsg('')}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="api2-kpis">
        <div className="api2-kpiCard">
          <div className="api2-kpiIcon ok"><FaCheckCircle /></div>
          <div>
            <div className="api2-kpiValue">{metrics.total}</div>
            <div className="api2-kpiLabel">Requisições (janela)</div>
            <div className="api2-kpiSub">{metrics.last ? `Última: ${metrics.last}` : 'Sem tráfego'}</div>
          </div>
        </div>

        <div className="api2-kpiCard">
          <div className="api2-kpiIcon bad"><FaExclamationTriangle /></div>
          <div>
            <div className="api2-kpiValue">{metrics.errors}</div>
            <div className="api2-kpiLabel">Erros (4xx/5xx)</div>
            <div className="api2-kpiSub">Acompanhe spikes e falhas</div>
          </div>
        </div>

        <div className="api2-kpiCard">
          <div className="api2-kpiIcon neutral"><FaClock /></div>
          <div>
            <div className="api2-kpiValue">{metrics.p95}ms</div>
            <div className="api2-kpiLabel">Latência p95 (aprox.)</div>
            <div className="api2-kpiSub">Média: {metrics.avg}ms</div>
          </div>
        </div>

        <div className="api2-kpiCard">
          <div className="api2-kpiIcon neutral"><FaNetworkWired /></div>
          <div>
            <div className="api2-kpiValue">{metrics.topVal}</div>
            <div className="api2-kpiLabel">Top endpoint (volume)</div>
            <div className="api2-kpiSub">{metrics.topName}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="api2-filters">
        <div className="api2-search">
          <FaSearch />
          <input
            placeholder="Buscar em endpoint, path, status, método..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="api2-filterGroup">
          <FaFilter className="api2-filterIcon" />

          <select value={endpointFilter} onChange={(e) => setEndpointFilter(e.target.value)}>
            <option value="">Todos endpoints</option>
            {endpoints.map(e => (
              <option key={e.id} value={e.id}>{e.nome}</option>
            ))}
          </select>

          <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
            <option value="">Todos métodos</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>

          <select value={statusFamily} onChange={(e) => setStatusFamily(e.target.value)}>
            <option value="">Todos status</option>
            <option value="2xx">2xx</option>
            <option value="4xx">4xx</option>
            <option value="5xx">5xx</option>
          </select>

          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={50}>50</option>
            <option value={120}>120</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>

          {(endpointFilter || methodFilter || statusFamily || search) && (
            <button
              className="api2-btn api2-btnGhost"
              onClick={() => { setEndpointFilter(''); setMethodFilter(''); setStatusFamily(''); setSearch(''); }}
              title="Limpar filtros"
            >
              <FaTimes /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Main Grid */}
      <div className="api2-grid">
        {/* Endpoints */}
        <section className="api2-card">
          <div className="api2-cardHeader">
            <h2>Endpoints (Allowlist)</h2>
            <span className="api2-muted">Dica: mantenha inventário e governança de APIs</span>
          </div>

          {loading ? (
            <div className="api2-loading">Carregando...</div>
          ) : (
            <div className="api2-tableWrap">
              <table className="api2-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Nome</th>
                    <th>Base URL</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoints.map(ep => (
                    <tr key={ep.id}>
                      <td><code>{ep.endpoint_key}</code></td>
                      <td className="api2-strong">{ep.nome}</td>
                      <td className="mono">{ep.base_url}</td>
                      <td>
                        <span className={pillClass(ep.enabled ? 'on' : 'off')}>
                          {ep.enabled ? 'ON' : 'OFF'}
                        </span>
                      </td>
                      <td className="api2-actionsCol">
                        <button className="api2-miniBtn" onClick={() => toggleEnabled(ep)}>
                          {ep.enabled ? <FaToggleOff /> : <FaToggleOn />} {ep.enabled ? 'Desabilitar' : 'Habilitar'}
                        </button>
                        <button className="api2-miniBtn api2-miniBtnGhost" onClick={() => openEditEndpoint(ep)}>
                          <FaEdit /> Editar
                        </button>
                        <button className="api2-miniBtn api2-miniBtnDanger" onClick={() => deleteEndpoint(ep)}>
                          <FaTrash /> Excluir
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!endpoints.length && (
                    <tr><td colSpan="5" className="api2-empty">Nenhum endpoint cadastrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="api2-footnote">
            Proxy: <code>/api/api-gateway/proxy/:endpointKey/*splat</code>
            <button className="api2-miniBtn" onClick={() => copyToClipboard('/api/api-gateway/proxy/:endpointKey/*splat', 'Proxy copiado!')}>
              <FaCopy /> Copiar
            </button>
          </div>
        </section>

        {/* Traffic */}
        <section className="api2-card">
          <div className="api2-cardHeader">
            <h2>Tráfego</h2>
            <span className="api2-muted">{filteredTraffic.length} de {traffic.length}</span>
          </div>

          {loading ? (
            <div className="api2-loading">Carregando...</div>
          ) : (
            <div className="api2-tableWrap">
              <table className="api2-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Endpoint</th>
                    <th>Método</th>
                    <th>Path</th>
                    <th>Status</th>
                    <th>ms</th>
                    <th>Quando</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTraffic.map(l => {
                    const st = Number(l.response_status || 0);
                    const stFamily = st >= 500 ? '5xx' : st >= 400 ? '4xx' : '2xx';

                    return (
                      <tr key={l.id}>
                        <td><code>{l.id}</code></td>
                        <td className="api2-strong">{l.endpoint_nome}</td>
                        <td><span className="pill">{l.method}</span></td>
                        <td className="mono api2-path">{l.path}{l.query_string ? `?${l.query_string}` : ''}</td>
                        <td><span className={pillClass(stFamily)}>{l.response_status}</span></td>
                        <td>{l.duration_ms}</td>
                        <td>{l.created_at}</td>
                        <td>
                          <button className="api2-link" onClick={() => openLog(l.id)}>Ver</button>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredTraffic.length && (
                    <tr><td colSpan="8" className="api2-empty">Nenhum tráfego registrado (ou filtros zeraram a lista).</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Drawer */}
      <div className={`api2-drawerBackdrop ${drawerOpen ? 'open' : ''}`} onClick={closeDrawer} />
      <aside className={`api2-drawer ${drawerOpen ? 'open' : ''}`}>
        <div className="api2-drawerHeader">
          <div>
            <div className="api2-drawerTitle">Log #{selectedLog?.id}</div>
            <div className="api2-drawerSub">
              <code>{selectedLog?.method}</code> • <code>{selectedLog?.path}</code> •
              <span className={pillClass(Number(selectedLog?.response_status) >= 400 ? '4xx' : '2xx')}>
                {selectedLog?.response_status}
              </span>
            </div>
          </div>

          <button className="api2-btn api2-btnGhost" onClick={closeDrawer}>
            <FaTimes /> Fechar
          </button>
        </div>

        <div className="api2-drawerBody">
          {selectedLog && (
            <>
              <div className="api2-drawerMeta">
                <div><b>Endpoint:</b> {selectedLog.endpoint_nome} (<code>{selectedLog.endpoint_key}</code>)</div>
                <div><b>Duração:</b> <code>{selectedLog.duration_ms}ms</code></div>
                <div><b>Quando:</b> {selectedLog.created_at}</div>
                {selectedLog.ip && <div><b>IP:</b> <code>{selectedLog.ip}</code></div>}
              </div>

              <div className="api2-jsonHeader">
                <h3>Request Headers</h3>
                <button className="api2-miniBtn" onClick={() => copyToClipboard(String(selectedLog.request_headers_json || ''), 'Request headers copiados!')}>
                  <FaCopy /> Copiar
                </button>
              </div>
              <pre className="api2-json">{prettyJSON(selectedLog.request_headers_json)}</pre>

              <div className="api2-jsonHeader">
                <h3>Request Body</h3>
                <button className="api2-miniBtn" onClick={() => copyToClipboard(String(selectedLog.request_body_json || ''), 'Request body copiado!')}>
                  <FaCopy /> Copiar
                </button>
              </div>
              <pre className="api2-json">{prettyJSON(selectedLog.request_body_json)}</pre>

              <div className="api2-jsonHeader">
                <h3>Response Headers</h3>
                <button className="api2-miniBtn" onClick={() => copyToClipboard(String(selectedLog.response_headers_json || ''), 'Response headers copiados!')}>
                  <FaCopy /> Copiar
                </button>
              </div>
              <pre className="api2-json">{prettyJSON(selectedLog.response_headers_json)}</pre>

              <div className="api2-jsonHeader">
                <h3>Response Body</h3>
                <button className="api2-miniBtn" onClick={() => copyToClipboard(String(selectedLog.response_body_json || ''), 'Response body copiado!')}>
                  <FaCopy /> Copiar
                </button>
              </div>
              <pre className="api2-json">{prettyJSON(selectedLog.response_body_json)}</pre>
            </>
          )}
        </div>
      </aside>

      {/* Endpoint Modal */}
      {epModalOpen && (
        <div className="api2-modalBackdrop" onClick={() => setEpModalOpen(false)}>
          <div className="api2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="api2-modalHeader">
              <h2>{editingEp ? 'Editar endpoint' : 'Novo endpoint'}</h2>
              <button className="api2-btn api2-btnGhost" onClick={() => setEpModalOpen(false)}>
                <FaTimes /> Fechar
              </button>
            </div>

            <div className="api2-modalBody">
              <div className="api2-formGrid">
                <div>
                  <label>endpoint_key</label>
                  <input
                    value={form.endpoint_key}
                    onChange={(e) => setForm(f => ({ ...f, endpoint_key: e.target.value }))}
                    placeholder="ex: porto"
                  />
                  <small className="api2-muted">Usado na rota: /proxy/porto/...</small>
                </div>

                <div>
                  <label>Nome</label>
                  <input
                    value={form.nome}
                    onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))}
                    placeholder="ex: Porto Seguro"
                  />
                </div>

                <div className="span2">
                  <label>Base URL</label>
                  <input
                    value={form.base_url}
                    onChange={(e) => setForm(f => ({ ...f, base_url: e.target.value }))}
                    placeholder="https://api.exemplo.com.br"
                  />
                </div>

                <div>
                  <label>Enabled</label>
                  <select value={form.enabled ? 1 : 0} onChange={(e) => setForm(f => ({ ...f, enabled: Number(e.target.value) === 1 }))}>
                    <option value={1}>ON</option>
                    <option value={0}>OFF</option>
                  </select>
                </div>

                <div>
                  <label>Métodos permitidos</label>
                  <div className="api2-checkRow">
                    {['GET','POST','PUT','DELETE'].map(m => (
                      <label key={m} className="api2-check">
                        <input
                          type="checkbox"
                          checked={!!methodsState[m]}
                          onChange={(e) => setMethodsState(s => ({ ...s, [m]: e.target.checked }))}
                        />
                        <span>{m}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="span2">
                  <label>Headers Template (JSON)</label>
                  <textarea
                    value={headersText}
                    onChange={(e) => setHeadersText(e.target.value)}
                    placeholder='{"x-api-key":"...","authorization":"Bearer ..."}'
                    rows={8}
                  />
                  <small className="api2-muted">
                    Dica: evite Basic Auth e prefira padrões (JWT/OAuth) + limites de taxa no gateway. [1](https://dmcard.sharepoint.com/sites/comunicacao-politicas--formularios-e-relatorios/Polticas/Seguran%c3%a7a%20da%20Informa%c3%a7%c3%a3o/NRM_-_SI_-_021_-_SEGURAN%c3%87A_PARA_O_DESENVOLVIMENTO_E_PUBLICA%c3%87%c3%83O_DE_SITE_E_API_vf_25.08.25.pdf?web=1)
                  </small>
                </div>
              </div>

              <div className="api2-modalFooter">
                <button className="api2-btn api2-btnGhost" onClick={() => setEpModalOpen(false)}>
                  <FaTimes /> Cancelar
                </button>
                <button className="api2-btn" onClick={saveEndpoint}>
                  <FaCheckCircle /> Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}